import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  VStack,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
} from "@chakra-ui/react";
import RightArrow from "../../assets/arrowCircleRight.svg";
import schoolAI from "../../assets/svgs/coschoolBlack.svg";
import checkMark from "../../assets/lotties/checkMark.json";
import correctMark from "../../assets/checkMark.svg";
import StudentImg from "../../assets/studentImg.svg";
import useStateRef from "react-usestateref";
import { StorageService } from "../../utils/StorageUtils";
import { getCookie, setCookie } from "../../utils/TutorConstants";
import { isNotEmptyOrNull } from "../../utils/common-utils";
import { useNavigate } from "@remix-run/react";
import { urlString } from "../../utils/common-utils";
import { getUserDetails, logoutUser } from "../../api/login-service";
import { ArrowBackIcon } from "@chakra-ui/icons";
import Lottie from "react-lottie-player";
import CoschoolMobile from "../../assets/svgs/coschoolMobile.svg";
const CardOption = ({ title, description, isSelected }) => (
  <Box
    width="100%"
    height={title === "Learn" ? "106px" : "90px"}
    paddingTop="16px"
    paddingRight="20px"
    paddingBottom="16px"
    paddingLeft="20px"
    borderRadius="8px"
    borderWidth="1px"
    display="flex"
    borderColor={"#A299D4"}
    justifyContent="space-between"
    alignItems="center"
    bg="white"
    mb={4}
  >
    <Box display="flex" alignItems="center">
      {isSelected && (
        <Image
          src={correctMark}
          alt="Selected"
          boxSize="24px"
          marginRight="10px"
        />
      )}
      <Box pr={"10px"}>
        <Text
          fontFamily="Poppins"
          fontWeight="600"
          fontSize="14px"
          lineHeight="20px"
          marginTop={"8px"}
          marginBottom={"8px"}
          letterSpacing="0%"
          color="purple.600"
        >
          {title}
        </Text>
        <Text
          fontFamily="Poppins"
          fontWeight="400"
          fontSize="12px"
          lineHeight="16px"
          marginBottom={"8px"}
          letterSpacing="0%"
          color="purple.600"
        >
          {description}
        </Text>
      </Box>
    </Box>
    {!isSelected && <Image src={RightArrow} alt="Right arrow" boxSize="24px" />}
  </Box>
);

const DemoOnBoarding = ({
  isOpen,
  onClose,
  handleDemoItemClick,
  handleCloseDemoClick,
  userDetailsData,
}) => {
  const [userDetails, setUserDetails, userDetailsRef] = useStateRef({});
  const [selectedCards, setUserSelectedCards, selectedCardsRef] = useStateRef(
    []
  );
  const [applyTransition, setApplyTransition] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [profileRefresh, setProfileRefresh] = useState(0);
  const bottomRef = useRef(null);
  useEffect(() => {
    if (applyTransition && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [applyTransition]);
  useEffect(() => {
    let selectedItems = getCookie("selectedDemoItems");

    // Check if selectedItems is a string and parse it if needed
    if (typeof selectedItems === "string") {
      try {
        selectedItems = JSON.parse(selectedItems);
      } catch (error) {
        console.error("Error parsing selectedItems cookie:", error);
        selectedItems = [];
      }
    }

    // If selectedItems is null, undefined, or not an array, set it to an empty array
    if (!selectedItems || !Array.isArray(selectedItems)) {
      selectedItems = [];
    }

    setUserSelectedCards(selectedItems);
  }, [userDetailsData]);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    let details = StorageService.getItem("user_details");
    details = typeof details === "object" ? details : JSON.parse(details);
    if (isNotEmptyOrNull(details?.userId)) {
      let userDetails = await getUserDetails(
        details?.userId,
        details?.registrationStatus
      );
      setUserDetails(userDetails?.data);
      setProfileRefresh((prev) => prev + 1);
    }
  };
  // Add effect to handle transition when all cards are selected
  useEffect(() => {
    if (selectedCardsRef.current?.length === 3) {
      // First delay to start the transition
      const transitionDelay = setTimeout(() => {
        setApplyTransition(true);
      }, 1000);

      // Second delay to show congratulations message
      const congratsDelay = setTimeout(() => {
        setShowCongrats(true);
      }, 1000); // Add enough time for the transition to complete

      return () => {
        clearTimeout(transitionDelay);
        clearTimeout(congratsDelay);
      };
    }
  }, [selectedCardsRef.current?.length]);

  const formatGrade = (gradeName) => {
    // Assuming the grade is stored in the format "Grade X" or just "X"
    const grade = gradeName?.replace(/\D/g, ""); // Remove all non-digit characters
    if (!grade) return gradeName; // Return original string if no digits found
    return `${grade}th Grade`; // Append "th Grade" to the digit
  };

  const navigate = useNavigate();

  const handleLogout = async (event) => {
    let { success, data, errorMessage } = await logoutUser();
    if (success) {
      //let { success, data, errorMessage } = data || {};
    } else {
      console.log(errorMessage, "Logout API failed");
    }
    StorageService.clear();

    setCookie("schoolId", "");
    window.location.href = `${window?.ENV?.BROWSER_URL}/demoProfiles`;
  };

  const handleSelectCard = (cardType) => {
    if (!selectedCardsRef.current?.includes(cardType)) {
      handleDemoItemClick(cardType);
    }
  };

  const ProfileSection = React.memo(({ userDetails }) => (
    <Flex
      bg={"#5F4DC7"}
      width="100%"
      mb={10}
      pt={14} // Increased top padding to prevent URL bar overlap
      pb={4}
      paddingRight={4}
      justifyContent="space-between"
      alignItems="center"
      position="relative" // Added position relative
      zIndex={1} // Added zIndex to ensure it's above other content
      safeAreaTop // If using SafeAreaView or similar component
    >
      <Image
        src={CoschoolMobile}
        alt="School logo"
        width={"42px"}
        height={"24px"}
        marginLeft={4}
      />

      <Flex alignItems="center">
        <Box textAlign="right" mr={2}>
          <Text fontSize="16px" fontWeight="500" color="white">
            {userDetails?.data?.firstName}
          </Text>
          {isNotEmptyOrNull(userDetails?.data?.schoolGradeName) && (
            <Text fontSize="12px" fontWeight="500" color="white">
              {`${formatGrade(userDetails?.data?.schoolGradeName)}${
                userDetails?.data?.sectionName
                  ? ` - ${userDetails?.data?.sectionName}`
                  : ""
              }`}
            </Text>
          )}
        </Box>
        <Image
          src={StudentImg}
          alt="Student profile"
          marginTop={"10px"}
          width={"50px"}
          height={"50px"}
          borderRadius="full"
        />
      </Flex>
    </Flex>
  ));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
      <ModalOverlay bg="purple.50" />
      <ModalContent
        bg="purple.50"
        maxW="100vw"
        h="100vh"
        m="0"
        borderRadius="0"
        boxShadow="none"
        pt="env(safe-area-inset-top)" // Add CSS environment variable for safe area
        overflowY="auto" // Ensure content is scrollable
        display="flex"
        flexDirection="column"
      >
        <ProfileSection
          key={profileRefresh}
          userDetails={userDetailsRef.current}
        />

        <ModalBody flex="1" display="flex" flexDirection="column">
          <VStack spacing={4} align="stretch">
            <Text
              fontFamily="Poppins"
              fontWeight="400"
              fontSize="14px"
              lineHeight="18px"
              marginBottom={"8px"}
              letterSpacing="0%"
              color="#432677"
            >
              Hey {userDetails?.data?.firstName}, your teacher has assigned you
              homework. Vin is here to guide you through it and help you learn
              better!
            </Text>
            <Box onClick={() => handleSelectCard("GP")}>
              <CardOption
                title="Guided Practice"
                description="Reinforce classroom learning with interactive worksheets and guided help from Vin."
                isSelected={selectedCardsRef.current?.includes("GP")}
              />
            </Box>
            <Box
              marginTop={applyTransition ? "-114px" : "0px"}
              transition="margin-top 0.6s ease-in-out"
              onClick={() => handleSelectCard("AP")}
            >
              <CardOption
                title="Assessment Practice"
                description="Build test readiness with practice papers, get instant feedback, and discuss with Vin."
                isSelected={selectedCardsRef.current?.includes("AP")}
              />
            </Box>
            <Box
              marginTop={applyTransition ? "-114px" : "0px"}
              transition="margin-top 0.6s ease-in-out"
              onClick={() => handleSelectCard("Learn")}
            >
              <CardOption
                title="Learn"
                description="Learn topics with videos and progress with Vin-guided factual, understanding & application based questions."
                isSelected={selectedCardsRef.current?.includes("Learn")}
              />
            </Box>
          </VStack>

          {showCongrats && selectedCardsRef.current?.length === 3 && (
            <VStack
              opacity={showCongrats ? 1 : 0}
              transition="opacity 0.5s ease-in"
            >
              <Lottie
                loop
                animationData={checkMark}
                play
                style={{ width: "130px", height: "130px" }}
              />
              <Box
                fontFamily="Poppins"
                fontWeight="600"
                fontSize="14px"
                lineHeight="20px"
                mb={"8px"}
              >
                Congratulations!
              </Box>
              <Box
                fontFamily="Poppins"
                fontWeight="400"
                textAlign={"center"}
                fontSize="12px"
                lineHeight="16px"
              >
                You've successfully completed the preview of the SchoolAi
                Student Platform and explored its core capabilities. You can now
                continue exploring or switch roles to see how SchoolAi empowers
                others across the school ecosystem.
              </Box>
              <Button
                mt="50px"
                w={"90%"}
                bg="purple.500"
                color="white"
                onClick={() => {
                  setCookie("IsDemo", false);
                  handleCloseDemoClick();
                }}
              >
                Continue Exploring
              </Button>
              <Button
                mt="30px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => {
                  setCookie("IsDemo", false);
                  handleLogout();
                }}
              >
                <Icon as={ArrowBackIcon} mr="2" />
                Go Back to Login Page
              </Button>
              <Image src={schoolAI} height="42px" marginTop={"10px"} />
              <div ref={bottomRef} />
            </VStack>
          )}
          {!showCongrats && (
            <Image
              src={schoolAI}
              height="42px"
              bottom={"24px"}
              position="fixed"
              left="50%"
              transform="translateX(-50%)"
              mb={4}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DemoOnBoarding;
